import { DOCUMENT } from "@angular/common";
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ValidationResult, Validator } from "@lchemy/model/validation";
import { interval } from "rxjs";
import {
  filter,
  finalize,
  first,
  map,
  pairwise,
  startWith,
  takeUntil
} from "rxjs/operators";

import { FORM_ERROR_FORMATTER_MAP } from "@lantern/app/common";
import { GrowlService } from "@lantern/app/core/utils/growl-service/growl.service";
import { AlertModalService } from "@lantern/app/core/utils/modals/alert-modal.service";

import { RedirectService } from "@local/app/common/redirect.service";
import { GlobalAuthService } from "@local/app/core/global-auth";
import { GoogleAuthService } from "@local/app/core/google-auth";
import { LoginCmd, loginCmdValidator } from "@local/app/models";
import { LOGIN_ERROR_MAP } from "@local/app/pages/shared";

const DEFAULT_REDIRECT = "/marts";
const BASE_LOCKOUT_KEY = "ta-lockout";

@Component({
  templateUrl: "./login.template.html",
  providers: [
    {
      provide: FORM_ERROR_FORMATTER_MAP,
      useValue: LOGIN_ERROR_MAP
    }
  ]
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild("googleSignInContainer") googleSignInContainer: any;
  @ViewChild("googleSignInMobileContainer") googleSignInMobileContainer: any;

  loading = false;

  lockedOutUntil: number | null = null;

  loginMethod?: string;
  redirect?: string;

  loginForm: FormGroup = this.formBuilder.group({
    email: "",
    password: "",
    recaptchaResponse: ""
  });
  loginFormValidator: Validator<LoginCmd> = loginCmdValidator;
  validationResult: ValidationResult;
  forceShowErrors: boolean = false;

  private windowRef: Window;

  get queryParams(): { email?: string; redirect?: string } {
    return {
      email: this.loginForm.value.email.length
        ? this.loginForm.value.email
        : undefined,
      redirect:
        this.redirect == null
          ? undefined
          : this.redirectService.sanitizeRedirectUrl(this.redirect)
    };
  }

  get lockedOutTooltipMessage(): string {
    return `Too many failed login attempts. Locked out until ${new Date(
      this.lockedOutUntil!
    )}`;
  }

  constructor(
    private growlService: GrowlService,
    private globalAuthService: GlobalAuthService,
    private googleAuthService: GoogleAuthService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    @Inject(DOCUMENT) documentRef: any,
    private formBuilder: FormBuilder,
    private ngZone: NgZone,
    private redirectService: RedirectService,
    private alertModalService: AlertModalService
  ) {
    this.windowRef = (documentRef as Document).defaultView!;
  }

  ngOnInit(): void {
    this.initFormValuesFromRouteParams();
    this.initValidation();
    this.initGoogleAuthServiceSubscription();
    this.initLockoutSubscription();
  }

  ngAfterViewInit(): void {
    this.renderGoogleButtons();
  }

  private onDestroyEmitter = new EventEmitter();
  ngOnDestroy(): void {
    this.onDestroyEmitter.emit();
    this.onDestroyEmitter.complete();
  }

  setRecaptchaResponse(recaptchaResponse: string): void {
    this.loginForm.patchValue({ recaptchaResponse });
  }

  setRecaptchaWrapperClass(visible: boolean): void {
    this.recaptchaWrapperClass = visible ? "pv--4" : "";
  }
  recaptchaWrapperClass = "";

  private renderGoogleButtons(): void {
    this.googleAuthService.renderButton(
      this.googleSignInContainer.nativeElement,
      "400px"
    );

    this.googleAuthService.renderButton(
      this.googleSignInMobileContainer.nativeElement,
      "360px"
    );
  }

  private initGoogleAuthServiceSubscription(): void {
    this.googleAuthService.credentials$
      .pipe(
        startWith(""),
        pairwise(),
        filter(
          ([oldCredentials, newCredentials]) =>
            oldCredentials !== newCredentials
        ),
        map(([_, newCredentials]) => newCredentials),
        takeUntil(this.onDestroyEmitter)
      )
      .subscribe((credential) => {
        if (!this.loading) {
          this.loading = true;
          this.globalAuthService
            .loginWithGoogle(credential)
            .pipe(
              finalize(() => (this.loading = false)),
              takeUntil(this.onDestroyEmitter)
            )
            .subscribe(
              () => this.performRedirect(),
              () => {
                // TODO: server errors
                this.growlService.open("loginError");
              }
            );
        }
      });
  }

  private initLockoutSubscription(): void {
    interval(1000)
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe(() => {
        const lockout = localStorage.getItem(this.lockoutKey());

        if (lockout == null) {
          this.lockedOutUntil = null;
          return;
        }

        const lockedOutUntil = parseInt(lockout);

        if (lockedOutUntil > Date.now()) {
          this.lockedOutUntil = lockedOutUntil;
        } else {
          this.removeLockout();
          this.lockedOutUntil = null;
        }
      });
  }

  async submitForm(): Promise<void> {
    if (this.loading) {
      return;
    }

    await this.validate();
    if (!this.validationResult.isValid) {
      this.forceShowErrors = true;
      this.growlService.open("formError");
      return;
    }

    this.callLogin();
  }

  private callLogin(): void {
    const { email, password, recaptchaResponse } = this.loginForm.value;

    this.loading = true;
    this.globalAuthService
      .loginWithPassword(email, password, recaptchaResponse)
      .subscribe(
        () => {
          this.removeLockout(email);
          this.alertModalService.open({
            title: "Check your email",
            message:
              "We've sent a one-time login link to the email address associated with your account. Click the link in the email to log in."
          });
        },
        async (e) => {
          if (
            e.status === 429 &&
            e.error.message.includes("Too many recent failed login attempts.")
          ) {
            this.growlService.open({
              type: "danger",
              message: e.error.message
            });
            this.setLockout(e.error.message);
          } else if (
            e.status === 403 &&
            e.error.message.includes("Your password has expired")
          ) {
            this.growlService.open({
              type: "danger",
              message: e.error.message
            });
            await this.router.navigateByUrl("/reset-password");
          } else if (
            e.status === 502 &&
            e.error.message.includes("Failed to send email")
          ) {
            this.growlService.open({
              type: "danger",
              message:
                "Failed to send magic link email. Please contact an administrator or try again later."
            });
          } else {
            this.growlService.open("loginError");
          }
          this.loading = false;
        }
      );
  }

  private performRedirect(): void {
    if (this.redirect != null) {
      this.redirectService.redirect(this.redirect);
      return;
    }

    this.globalAuthService
      .getMarts()
      .pipe(first(), takeUntil(this.onDestroyEmitter))
      .subscribe((marts) => {
        if (marts.length === 1) {
          this.windowRef.location.href = `//${marts[0].domain}`;
        } else {
          this.ngZone.run(() => this.router.navigateByUrl(DEFAULT_REDIRECT));
        }
      });
  }

  private initFormValuesFromRouteParams(): void {
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe((params) => {
        this.loginForm.patchValue({ email: params.email || "" });
        this.redirect = params.redirect;

        switch (params.method) {
          case "tapad":
            this.loginMethod = params.method;
            break;
          default:
            this.loginMethod = undefined;
            break;
        }
      });
  }

  private initValidation(): void {
    this.loginForm.valueChanges
      .pipe(startWith(true), takeUntil(this.onDestroyEmitter))
      .subscribe(() => this.validate());
  }

  async validate(): Promise<void> {
    this.validationResult = await this.loginFormValidator.validate(
      this.loginForm.value
    );
  }

  private setLockout(error: string): void {
    const unlockTime = new Date(error.split("Account locked until ")[1]);
    localStorage.setItem(this.lockoutKey(), unlockTime.valueOf().toString());
  }

  private removeLockout(email?: string): void {
    localStorage.removeItem(this.lockoutKey(email));
  }

  private lockoutKey(email?: string): string {
    const currentEmail = email || this.loginForm.get("email")!.value;
    return `${BASE_LOCKOUT_KEY}-${currentEmail}`;
  }
}
